import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { PasswordInput } from "../components/shared/PasswordInput";

const roleLanding = (role) => {
  if (role === 'admin') return '/admin';
  if (role === 'provider') return '/provider/dashboard';
  if (role === 'customer') return '/my-quotes';
  return '/';
};

const LoginPage = () => {
  const { signIn, resendVerification, isAuthenticated, isRecoveryMode, recoveryError, profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState('');
  const [verifiedNotice, setVerifiedNotice] = useState(false);

  const redirect = searchParams.get('redirect');

  useEffect(() => {
    // Show the ✅ notice after clicking the email confirmation link
    if (searchParams.get('verified') === '1') {
      setVerifiedNotice(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated && !isRecoveryMode && !recoveryError) {
      navigate(redirect || roleLanding(profile?.role));
    }
  }, [isAuthenticated, isRecoveryMode, recoveryError, redirect, profile?.role, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowResend(false);
    setResendResult('');
    setLoading(true);

    const { data, error } = await signIn({ email, password });

    if (error) {
      const msg = error.message || 'Invalid email or password.';
      if (msg.toLowerCase().includes('verify') || msg.toLowerCase().includes('not confirmed')) {
        setError('Please verify your email before continuing. Check your inbox (and spam folder) for the HandyHub confirmation link.');
        setShowResend(true);
      } else {
        setError(msg);
      }
    } else if (data?.profile?.role) {
      navigate(redirect || roleLanding(data.profile.role));
    }

    setLoading(false);
  };

  const handleResend = async () => {
    if (!email) {
      setResendResult('Enter your email above first.');
      return;
    }
    setResending(true);
    setResendResult('');
    const { error } = await resendVerification(email);
    if (error) {
      setResendResult(error.message || 'Could not resend verification email. Please try again in a moment.');
    } else {
      setResendResult('Verification email sent. Please check your inbox (and spam folder).');
    }
    setResending(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Welcome Back</h1>
        <p>Sign in to your HandyHub account</p>

        {verifiedNotice && !error && (
          <div className="success-message" data-testid="login-verified-notice" style={{ background: '#d1fae5', border: '1px solid #34d399', color: '#065f46', padding: '10px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '0.875rem' }}>
            Email verified. Sign in below to continue.
          </div>
        )}

        {error && <div className="error-message" data-testid="login-error">{error}</div>}

        {showResend && (
          <div data-testid="resend-verification-block" style={{ marginTop: '-4px', marginBottom: '12px', padding: '12px 14px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '6px', fontSize: '0.875rem' }}>
            <p style={{ margin: '0 0 8px', color: '#92400e' }}>Didn&apos;t get the email? We&apos;ll send you a fresh confirmation link.</p>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="btn-outline"
              data-testid="login-resend-verification-btn"
              style={{ padding: '8px 14px', fontSize: '0.875rem' }}
            >
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>
            {resendResult && (
              <p data-testid="resend-verification-result" style={{ margin: '8px 0 0', fontSize: '0.8125rem', color: resendResult.toLowerCase().startsWith('verification email sent') ? '#065f46' : '#92400e' }}>
                {resendResult}
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form" data-testid="login-form">
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              data-testid="email-input"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              data-testid="password-input"
            />
          </div>
          <button 
            type="submit" 
            className="btn-primary btn-full"
            disabled={loading}
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/forgot-password" className="auth-forgot-link" data-testid="forgot-password-link">Forgot your password?</Link>
        </div>

        <p className="auth-footer">
          Don&apos;t have an account? <Link to="/signup">Sign up</Link>
        </p>
      </div>
    </div>
  );
};


export default LoginPage;
